import * as Joi from "joi";
import moment from 'moment';
import {
	Column,
	Entity,
	JoinColumn,
	ManyToOne,
	PrimaryGeneratedColumn,
} from 'typeorm';
import { booleanTransformer, GenPartial, momentTransformer } from 'typeormgen';
import Base from '../base.entity';
import { PLANS } from '../enums/enums';
import Episodic from '../episodics/episodic.entity';

type Moment = moment.Moment;

@Entity('department_settings')
export default class DepartmentSetting extends Base {
	static readonly tableName = 'department_settings';

	@PrimaryGeneratedColumn()
	id: number;

	@Column('int')
	episodic_id: number;

	@Column('int')
	dept_id: number;

	@Column('int')
	user_limit: number;

	@Column('int')
	device_limit: number;

	@Column({ type: 'int', transformer: booleanTransformer })
	add_users: boolean;

	@Column('int')
	plan: number;

	@Column({ type: 'int', transformer: booleanTransformer })
	script_upload_disabled: boolean;

	@Column({ type: 'datetime', transformer: momentTransformer })
	start_date: Moment;

	@Column({ type: 'datetime', transformer: momentTransformer })
	end_date: Moment;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	last_updated_time: Moment;

	@Column('int')
	last_updated_user: number;

	@Column('int')
	created_by: number;

	@Column({ type: 'timestamp', transformer: momentTransformer })
	date_created: Moment;

	@ManyToOne((type) => Episodic, (episodic) => episodic.departmentSettings)
	@JoinColumn({
		name: 'episodic_id',
	})
	episodic: Episodic;

	static rules() {
		return {
			id: Joi.number().integer(),
			episodic_id: Joi.number().integer(),
			dept_id: Joi.number().integer(),
			user_limit: Joi.number().integer().allow(null),
			device_limit: Joi.number().integer().allow(null),
			add_users: Joi.boolean(),
			plan: Joi.number().integer(),
			script_upload_disabled: Joi.boolean(),
			start_date: Joi.date(),
			end_date: Joi.date(),
			last_updated_time: Joi.date().allow(null),
			last_updated_user: Joi.number().integer(),
			created_by: Joi.number().integer(),
			date_created: Joi.date().allow(null),
		};
	}

	toJSON() {
		return {
			id: this.id,
			episodic_id: this.episodic_id,
			dept_id: this.dept_id,
			user_limit: this.user_limit,
			device_limit: this.device_limit,
			add_users: this.add_users,
			plan: this.plan,
			script_upload_disabled: this.script_upload_disabled,
			start_date: this.start_date,
			end_date: this.end_date,
			last_updated_time: this.last_updated_time,
			last_updated_user: this.last_updated_user,
			created_by: this.created_by,
			date_created: this.date_created,
		};
	}

	assign(props?: GenPartial<DepartmentSetting>) {
		super.assign(props);

		if (props) {
			if (typeof props.add_users !== 'undefined') {
				this.add_users = booleanTransformer.from(props.add_users);
			}
			if (typeof props.script_upload_disabled !== 'undefined') {
				this.script_upload_disabled = booleanTransformer.from(
					props.script_upload_disabled
				);
			}
			if (typeof props.start_date !== 'undefined') {
				this.start_date = momentTransformer.from(props.start_date);
			}
			if (typeof props.end_date !== 'undefined') {
				this.end_date = momentTransformer.from(props.end_date);
			}
			if (typeof props.last_updated_time !== 'undefined') {
				this.last_updated_time = momentTransformer.from(
					props.last_updated_time
				);
			}
			if (typeof props.date_created !== 'undefined') {
				this.date_created = momentTransformer.from(props.date_created);
			}
		}
	}

	constructor(props?: GenPartial<DepartmentSetting>) {
		super(props);
	}

	isExpired() {
		if (this.end_date) {
			let numDays = this.getNumDays();

			if (numDays === null) {
				const endDate = this.end_date.clone();
				// if the end date is not valid (ie 0000-00-00 00:00:00)
				// than it can't expire
				endDate.add(14, 'hours');
				return endDate.isValid() && moment.utc().isAfter(endDate);
			} else {
				return numDays === 0;
			}
		}
		return false;
	}

	getNumDays() {
		let isEpisodicOnFreeTrial: boolean = true;
		let diffDays = 0;

		if (this.plan !== 0) {
			isEpisodicOnFreeTrial = false;
			if (this.end_date !== null) {
				let end_date = moment(this.end_date, 'YYYY-MM-DD');
				end_date.add(14, 'hours');
				let start_today = moment(moment().format('YYYY-MM-DD'), 'YYYY-MM-DD');

				diffDays = Math.floor(
					moment.duration(end_date.diff(start_today)).asDays()
				);
			} else if (this.end_date === null) {
				diffDays = 300;
			}
		}
		if (isEpisodicOnFreeTrial) {
			return null;
		} else {
			return diffDays < 0 ? 0 : diffDays;
		}
	}

	isAboutToExpire() {
		if (this.end_date) {
			const endDate = this.end_date.clone();
			if (endDate.isValid()) {
				let today_date = moment().format('YYYY-MM-DD');
				let end_date = moment(this.end_date, 'YYYY-MM-DD');
				let start_today = moment(today_date, 'YYYY-MM-DD');
				let diffdays = moment.duration(end_date.diff(start_today)).asDays();
				return Math.floor(diffdays);
			}
		}
		return 8;
	}

	isValidForMoneyTracking() {
		const moneyTrackingDate = moment('2018-01-22 00:00:0');
		if (
			(this.plan === PLANS.FREE || this.plan === PLANS.LITE) &&
			this.date_created > moneyTrackingDate
		) {
			return false;
		}
		return true;
	}

	setLiteAccount() {
		this.plan = PLANS.LITE;
		this.add_users = true;
		this.script_upload_disabled = false;
		this.user_limit = 4;
		this.device_limit = null;
	}
}
